import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { anthropicCost, elevenCost, type AnthropicUsage } from '../lib/costs';

type AnthropicByModel = {
  calls: number;
  inputTokens: number;
  outputTokens: number;
  cacheCreationTokens: number;
  cacheReadTokens: number;
  cost: number;
};

type ElevenByModel = {
  calls: number;
  chars: number;
  cost: number;
};

type AnthropicTotals = {
  calls: number;
  inputTokens: number;
  outputTokens: number;
  cacheCreationTokens: number;
  cacheReadTokens: number;
  cost: number;
  byModel: Record<string, AnthropicByModel>;
};

type ElevenTotals = {
  calls: number;
  chars: number;
  cost: number;
  byModel: Record<string, ElevenByModel>;
};

type UsageState = {
  anthropic: AnthropicTotals;
  eleven: ElevenTotals;
  since: number;

  recordAnthropic: (model: string, usage: AnthropicUsage) => void;
  recordEleven: (model: string, chars: number) => void;
  reset: () => void;
};

function emptyAnthropic(): AnthropicTotals {
  return {
    calls: 0,
    inputTokens: 0,
    outputTokens: 0,
    cacheCreationTokens: 0,
    cacheReadTokens: 0,
    cost: 0,
    byModel: {},
  };
}

function emptyEleven(): ElevenTotals {
  return { calls: 0, chars: 0, cost: 0, byModel: {} };
}

export const useUsage = create<UsageState>()(
  persist(
    (set) => ({
      anthropic: emptyAnthropic(),
      eleven: emptyEleven(),
      since: Date.now(),

      recordAnthropic: (model, usage) =>
        set((s) => {
          const cost = anthropicCost(usage, model);
          const cc = usage.cache_creation_input_tokens ?? 0;
          const cr = usage.cache_read_input_tokens ?? 0;
          const prev = s.anthropic.byModel[model] ?? {
            calls: 0,
            inputTokens: 0,
            outputTokens: 0,
            cacheCreationTokens: 0,
            cacheReadTokens: 0,
            cost: 0,
          };
          return {
            anthropic: {
              calls: s.anthropic.calls + 1,
              inputTokens: s.anthropic.inputTokens + usage.input_tokens,
              outputTokens: s.anthropic.outputTokens + usage.output_tokens,
              cacheCreationTokens: s.anthropic.cacheCreationTokens + cc,
              cacheReadTokens: s.anthropic.cacheReadTokens + cr,
              cost: s.anthropic.cost + cost,
              byModel: {
                ...s.anthropic.byModel,
                [model]: {
                  calls: prev.calls + 1,
                  inputTokens: prev.inputTokens + usage.input_tokens,
                  outputTokens: prev.outputTokens + usage.output_tokens,
                  cacheCreationTokens: prev.cacheCreationTokens + cc,
                  cacheReadTokens: prev.cacheReadTokens + cr,
                  cost: prev.cost + cost,
                },
              },
            },
          };
        }),

      recordEleven: (model, chars) =>
        set((s) => {
          const cost = elevenCost(chars, model);
          const prev = s.eleven.byModel[model] ?? { calls: 0, chars: 0, cost: 0 };
          return {
            eleven: {
              calls: s.eleven.calls + 1,
              chars: s.eleven.chars + chars,
              cost: s.eleven.cost + cost,
              byModel: {
                ...s.eleven.byModel,
                [model]: {
                  calls: prev.calls + 1,
                  chars: prev.chars + chars,
                  cost: prev.cost + cost,
                },
              },
            },
          };
        }),

      reset: () =>
        set({
          anthropic: emptyAnthropic(),
          eleven: emptyEleven(),
          since: Date.now(),
        }),
    }),
    { name: 'bedtimer-usage', version: 1 }
  )
);
